<template>
  <div class="teacher-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="8" class="text-center">
            <h1 class="hero-title mb-2">원어민 선생님</h1>
            <div class="hero-subtitle mb-2">Our Native English Teachers</div>
            <p class="hero-description">Learn from certified teachers with perfect pronunciation and extensive experience</p>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Info Section -->
    <section class="teacher-info-section">
      <v-container>
        <v-row justify="center">
          <v-col cols="12">
            <v-card class="info-card mb-6" rounded="lg" elevation="3">
              <div class="card-content">
                <div class="feature-grid">
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <div class="feature-item">
                        <div class="card-title-container">
                          <div class="icon-wrapper">
                            <v-icon color="primary" size="20">mdi-certificate</v-icon>
                          </div>
                          <div class="title-content">
                            <h4 class="feature-title">전문 자격증 보유</h4>
                            <p class="feature-desc">TESOL/TEFL 자격증을 갖춘 전문 교육자</p>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="feature-item">
                        <div class="card-title-container">
                          <div class="icon-wrapper">
                            <v-icon color="primary" size="20">mdi-briefcase</v-icon>
                          </div>
                          <div class="title-content">
                            <h4 class="feature-title">풍부한 경력</h4>
                            <p class="feature-desc">전화영어센터 및 교육기관 3년 이상 경력 보유</p>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="feature-item">
                        <div class="card-title-container">
                          <div class="icon-wrapper">
                            <v-icon color="primary" size="20">mdi-check-decagram</v-icon>
                          </div>
                          <div class="title-content">
                            <h4 class="feature-title">철저한 검증 과정</h4>
                            <p class="feature-desc">한국인 교육 담당자의 엄격한 발음 및 교수법 평가</p>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="feature-item">
                        <div class="card-title-container">
                          <div class="icon-wrapper">
                            <v-icon color="primary" size="20">mdi-account-switch</v-icon>
                          </div>
                          <div class="title-content">
                            <h4 class="feature-title">맞춤형 수업</h4>
                            <p class="feature-desc">수강 기간 내 담당 선생님 지정 및 수업 시간 조율 가능</p>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="feature-item">
                        <div class="card-title-container">
                          <div class="icon-wrapper">
                            <v-icon color="primary" size="20">mdi-star</v-icon>
                          </div>
                          <div class="title-content">
                            <h4 class="feature-title">학습자 중심 교육</h4>
                            <p class="feature-desc">학습자의 목표와 필요에 따른 맞춤형 학습 설계</p>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="feature-item">
                        <div class="card-title-container">
                          <div class="icon-wrapper">
                            <v-icon color="primary" size="20">mdi-teach</v-icon>
                          </div>
                          <div class="title-content">
                            <h4 class="feature-title">지속적인 피드백</h4>
                            <p class="feature-desc">진도 관리 및 효과적인 학습 피드백 제공</p>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Teachers Section -->
    <section class="teachers-section">
      <v-container>
        <v-row>
          <v-col cols="12" class="mb-4">
            <h2 class="section-title text-center">Meet Our Teachers</h2>
            <p class="section-subtitle text-center">모든 선생님은 영어 교육에 대한 열정과 전문성을 갖추고 있습니다</p>
          </v-col>
        </v-row>
        
        <v-row>
          <v-col cols="12" sm="6" md="4" v-for="t in teacherList" :key="t.accountId" class="mb-5">
            <v-card class="teacher-card" rounded="lg" elevation="2" hover>
              <div class="teacher-card-content">
                <div class="teacher-img-container">
                  <v-img 
                    :src="t.profileImageUrl" 
                    height="240"
                    class="teacher-profile-img"
                    cover
                  />
                </div>
                
                <div class="teacher-info">
                  <div class="d-flex justify-space-between align-center">
                    <h3 class="teacher-name">{{ t.username }}</h3>
                    <v-btn 
                      class="mobile-detail-btn" 
                      x-small
                      icon
                      color="primary"
                      @click="selectTeacher(t)"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </div>
                  
                  <div class="teacher-major">
                    <v-icon small color="primary" class="mr-1">mdi-school</v-icon>
                    <span>{{ t.major }}</span>
                  </div>
                  
                  <div class="teacher-personality mt-2">
                    <div class="personality-title">
                      <v-icon small color="primary" class="mr-1">mdi-account-heart</v-icon>
                      <span>Personality</span>
                    </div>
                    <p class="personality-text">{{ (t.personality || '').substring(0, 80) + '...' }}</p>
                  </div>
                  
                  <v-btn 
                    class="teacher-detail-btn mt-2 hidden-sm-and-down" 
                    color="primary"
                    rounded
                    small
                    elevation="1"
                    @click="selectTeacher(t)"
                  >
                    <v-icon small left>mdi-account-details</v-icon>
                    자세히 보기
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
    
    <DialogAccount
      :key="selectedAccount.accountId"
      :value="isShowAccountDialog"
      @input="isShowAccountDialog = $event"
      :selected-account="selectedAccount"
      :selected-account-id="selectedAccount.accountId"
      @hide="hideDialog"
    />
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import DialogAccount from "@/components/Dialog/DialogAccount.vue";

export default {
  name: 'Teacher',
  props: {
    id: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      selectedAccount: {},
      isShowAccountDialog: false,
      teacher: '' +
          '### 검증된 원어민 선생님\n\n' +
          '- **전문 자격증 보유** - TESOL/TEFL 자격증을 갖춘 전문 교육자\n' +
          '- **풍부한 경력** - 전화영어센터 및 교육기관 3년 이상 경력 보유\n' +
          '- **철저한 검증 과정** - 한국인 교육 담당자의 엄격한 발음 및 교수법 평가\n' +
          '- **맞춤형 수업** - 수강 기간 내 담당 선생님 지정 및 수업 시간 조율 가능\n\n' +
          '모든 선생님은 영어 교육에 대한 열정과 전문성을 갖추고 있으며, 학습자의 목표 달성을 위해 최선을 다하고 있습니다. 각 선생님의 프로필을 확인하시고 자신에게 맞는 선생님을 선택해보세요.',
      teacherList: []
    }
  },
  components: {
    DialogAccount
  },
  computed: {
    ...mapGetters(['app','user', 'isStudent', 'isTeacher', 'isManager']),
  },
  watch: {
    teacherList: {
      handler(newList) {
        if (this.id && newList.length > 0) {
          const teacher = newList.find(t => t.accountId === this.id)
          if (teacher) {
            this.selectTeacher(teacher)
          }
        }
      },
      immediate: true
    }
  },
  created() { },
  mounted() {
    this.fetchTeacherList()
      .then(res => {
        this.teacherList = res.sort((a, b) => {
          return a.profileVideoUrl > b.profileVideoUrl ? -1 : 1
        })
      })
  },
  methods: {
    ...mapActions(['fetchTeacherList']),
    selectTeacher(teacher){
      this.selectedAccount = teacher
      this.isShowAccountDialog = true
    },
    hideDialog() {
      this.searchTeacherDialog = false
      this.isShowAccountDialog = false
      if (this.id) {
        // Remove the ID from the route when closing dialog
        this.$router.push({ name: 'MainTeacher' })
      }
    },
  },
}
</script>

<style lang="scss">
@import "@/assets/scss/info-teacher.scss";
@import "@/assets/scss/elegant-theme.scss";

.teacher-page {
  background-color: #f9f9f9;
  min-height: 100vh;
}

.hero-section {
  padding: 50px 0 30px;

  
  .hero-title {
    font-size: 2.2rem;
    font-weight: 700;
    color: #334155;
    margin-bottom: 0.5rem;
    letter-spacing: -0.5px;
  }
  
  .hero-subtitle {
    font-size: 1.2rem;
    color: #3b82f6;
    font-weight: 600;
  }
  
  .hero-description {
    font-size: 1rem;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto;
  }
}

.teacher-info-section {
  padding: 10px 0 20px;
  margin-top: -15px;
  
  .info-card {
    overflow: hidden;
    transition: all 0.3s ease;
    
    .card-content {
      padding: 20px 15px 15px;
    }
    
    .feature-grid {
      width: 100%;
    }
    
    .feature-item {
      text-align: left;
      padding: 5px;
      height: 100%;
      max-height: 90px;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    
    .card-title-container {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
    }
    
    .icon-wrapper {
      background-color: rgba(59, 130, 246, 0.1);
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      flex-shrink: 0;
    }
    
    .title-content {
      flex: 1;
    }
    
    .feature-title {
      font-size: 0.9rem;
      font-weight: 600;
      color: #1e40af;
      margin-bottom: 3px;
      white-space: normal;
    }
    
    .feature-desc {
      color: #64748b;
      font-size: 0.8rem;
      line-height: 1.25;
      margin-bottom: 0;
    }
  }
}

.teachers-section {
  padding: 20px 0 60px;
  
  .section-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #334155;
    margin-bottom: 8px;
  }
  
  .section-subtitle {
    color: #64748b;
    font-size: 1rem;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .teacher-card {
    height: 100%;
    transition: all 0.3s ease;
    overflow: hidden;
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1) !important;
    }
    
    .teacher-card-content {
      padding: 0 0 15px;
    }
    
    .teacher-img-container {
      width: 100%;
      position: relative;
      margin-bottom: 12px;
    }
    
    .teacher-profile-img {
      transition: transform 0.5s ease;
    }
    
    .teacher-info {
      padding: 0 15px 5px;
    }
    
    .teacher-name {
      font-size: 1.3rem;
      font-weight: 600;
      color: #1e40af;
      margin-bottom: 5px;
    }
    
    .teacher-major {
      display: flex;
      align-items: center;
      color: #64748b;
      font-size: 0.9rem;
      margin-bottom: 5px;
    }
    
    .personality-title {
      display: flex;
      align-items: center;
      font-weight: 600;
      color: #334155;
      margin-bottom: 5px;
      font-size: 0.9rem;
    }
    
    .personality-text {
      color: #64748b;
      font-size: 0.85rem;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 0;
    }
    
    .teacher-detail-btn {
      text-transform: none;
      letter-spacing: 0;
      font-weight: 500;
      font-size: 0.85rem;
    }
    
    .mobile-detail-btn {
      margin-top: -5px;
    }
  }
}

// Dark mode support
.v-application.theme--dark {
  .teacher-page {
    background-color: #121212;
  }
  
  .hero-section {
    background: linear-gradient(135deg, #1a1a1a 0%, #2d3748 100%);
    
    .hero-title {
      color: #e2e8f0;
    }
    
    .hero-subtitle {
      color: #60a5fa;
    }
    
    .hero-description {
      color: #a0aec0;
    }
  }
  
  .teacher-info-section {
    .info-card {
      background-color: #1e1e1e !important;
      
      .feature-title {
        color: #60a5fa;
      }
      
      .feature-desc {
        color: #a0aec0;
      }
    }
  }
  
  .teachers-section {
    .section-title {
      color: #e2e8f0;
    }
    
    .section-subtitle {
      color: #a0aec0;
    }
    
    .teacher-card {
      background-color: #1e1e1e !important;
      
      .teacher-name {
        color: #60a5fa;
      }
      
      .teacher-major {
        color: #a0aec0;
      }
      
      .personality-title {
        color: #e2e8f0;
      }
      
      .personality-text {
        color: #a0aec0;
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 960px) {
  .hero-section {
    padding: 35px 0 25px;
    
    .hero-title {
      font-size: 1.8rem;
    }
    
    .hero-subtitle {
      font-size: 1rem;
    }
    
    .hero-description {
      font-size: 0.9rem;
      padding: 0 15px;
    }
  }
  
  .teacher-info-section {
    padding: 10px 0 20px;
    
    .card-content {
      padding: 20px 10px !important;
    }
    
    .feature-item {
      padding: 5px !important;
    }
    
    .icon-wrapper {
      width: 32px !important;
      height: 32px !important;
      margin-right: 10px !important;
    }
    
    .feature-title {
      font-size: 0.85rem !important;
    }
    
    .feature-desc {
      font-size: 0.75rem !important;
      line-height: 1.2 !important;
    }
  }
  
  .teachers-section {
    padding: 15px 0 40px;
    
    .section-title {
      font-size: 1.5rem;
    }
    
    .section-subtitle {
      font-size: 0.9rem;
      padding: 0 15px;
    }
    
    .teacher-card {
      margin-bottom: 10px;
      
      .teacher-img-container {
        margin-bottom: 10px;
      }
      
      .teacher-info {
        padding: 0 12px 5px;
      }
      
      .teacher-name {
        font-size: 1.1rem;
      }
      
      .teacher-major, .personality-title {
        font-size: 0.85rem;
      }
      
      .personality-text {
        font-size: 0.8rem;
        -webkit-line-clamp: 2;
      }
    }
  }
}

@media (max-width: 600px) {
  .teacher-info-section {
    .info-card {
      margin-bottom: 15px !important;
    }
    
    .feature-item {
      max-height: 80px !important;
      
      .icon-wrapper {
        width: 28px !important;
        height: 28px !important;
        margin-right: 8px !important;
      }
      
      .feature-title {
        font-size: 0.8rem !important;
      }
      
      .feature-desc {
        font-size: 0.7rem !important;
      }
    }
  }
  
  .teachers-section {
    .teacher-card {
      .teacher-profile-img {
        height: 200px !important;
      }
    }
  }
}
</style>

